/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type FormEvent, useEffect, useMemo, useRef, useCallback, type MutableRefObject } from 'react';
import { useBasket } from '@/providers/basket';
import { getDefaultShippingMethod } from '@/lib/customer-profile-utils';
import { useCustomerProfile } from '@/hooks/checkout/use-customer-profile';
import type { ShopperBasketsV2 } from '@salesforce/storefront-next-runtime/scapi';
import type { CheckoutActionData } from '../types';
import { getCheckoutDisplayError } from './checkout-display-error';

function useLatestRef<T>(value: T): MutableRefObject<T> {
    const ref = useRef(value);
    ref.current = value;
    return ref;
}

interface ShippingPromotion {
    calloutMsg?: string;
    promotionId?: string;
    promotionName?: string;
}

export interface ShippingMethod {
    id: string;
    name: string;
    description?: string;
    price: number;
    shippingPromotions?: ShippingPromotion[];
}

interface UseShippingOptionsParams {
    onSubmit: (formData: FormData) => void;
    isLoading: boolean;
    actionData?: CheckoutActionData;
    shippingMethods?: ShopperBasketsV2.schemas['ShippingMethodResult'];
    isEditing: boolean;
}

type SelectedMethod = NonNullable<
    NonNullable<ShopperBasketsV2.schemas['Basket']['shipments']>[number]
>['shippingMethod'];

export interface UseShippingOptionsReturn {
    availableShippingMethods: ShippingMethod[];
    selectedMethod: SelectedMethod;
    summaryMethod: ShippingMethod | undefined;
    defaultShippingMethodId: string | undefined;
    shippingOptionsError: string | undefined;
    isGuest: boolean;
    hideChangeForGuest: boolean;
    isUpcomingStep: boolean;
    getDiscountedPrice: (basePrice: number) => number;
    handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
}

export function useShippingOptions({
    onSubmit,
    isLoading,
    actionData,
    shippingMethods,
    isEditing,
}: UseShippingOptionsParams): UseShippingOptionsReturn {
    const cart = useBasket();
    const customerProfile = useCustomerProfile();

    const availableShippingMethods: ShippingMethod[] = useMemo(
        () =>
            shippingMethods?.applicableShippingMethods
                ?.filter((method): method is NonNullable<typeof method> & { id: string; name: string; price: number } =>
                    Boolean(method.id && method.name && typeof method.price === 'number' && !Number.isNaN(method.price))
                )
                .map((method) => ({
                    id: method.id,
                    name: method.name,
                    description: method.description,
                    price: method.price,
                    shippingPromotions: method.shippingPromotions,
                })) || [],
        [shippingMethods?.applicableShippingMethods]
    );

    const shippingDiscount = useMemo(() => {
        const adjustment = cart?.shippingItems?.[0]?.priceAdjustments?.[0];
        if (!adjustment?.appliedDiscount) return undefined;
        return adjustment.appliedDiscount;
    }, [cart?.shippingItems]);

    const getDiscountedPrice = useCallback(
        (basePrice: number): number => {
            if (!shippingDiscount) return basePrice;
            switch (shippingDiscount.type) {
                case 'free':
                    return 0;
                case 'percentage':
                    return basePrice * (1 - (shippingDiscount.amount ?? 0));
                case 'amount':
                    return Math.max(0, basePrice - (shippingDiscount.amount ?? 0));
                case 'fixed_price':
                    return shippingDiscount.amount ?? basePrice;
                default:
                    return basePrice;
            }
        },
        [shippingDiscount]
    );

    const selectedMethod = cart?.shipments?.[0]?.shippingMethod;

    const summaryMethod: ShippingMethod | undefined = useMemo(() => {
        if (!selectedMethod?.id) return undefined;
        const fromList = availableShippingMethods.find((m) => m.id === selectedMethod.id);
        if (fromList) return fromList;
        return {
            id: selectedMethod.id,
            name: selectedMethod.name ?? selectedMethod.id,
            description: selectedMethod.description,
            price: typeof selectedMethod.price === 'number' ? selectedMethod.price : 0,
            shippingPromotions: selectedMethod.shippingPromotions,
        };
    }, [selectedMethod, availableShippingMethods]);

    const isGuest = !customerProfile?.customer?.customerId;
    const hideChangeForGuest = isGuest && !selectedMethod;
    const isUpcomingStep = !isEditing && !selectedMethod;

    const defaultShippingMethodId = getDefaultShippingMethod(
        availableShippingMethods,
        selectedMethod,
        shippingMethods?.defaultShippingMethodId
    );

    const shippingOptionsError = getCheckoutDisplayError(actionData, 'shippingOptions');

    const onSubmitRef = useLatestRef(onSubmit);
    const hasAutoSubmitted = useRef(false);

    useEffect(() => {
        if (
            isEditing &&
            !selectedMethod?.id &&
            customerProfile &&
            availableShippingMethods.length > 0 &&
            !hasAutoSubmitted.current &&
            !isLoading
        ) {
            hasAutoSubmitted.current = true;

            const isDefaultValid =
                defaultShippingMethodId &&
                availableShippingMethods.some((method) => method.id === defaultShippingMethodId);
            const methodIdToSubmit = isDefaultValid ? defaultShippingMethodId : availableShippingMethods[0]?.id;

            if (methodIdToSubmit) {
                const formData = new FormData();
                formData.append('shippingMethodId', methodIdToSubmit);
                onSubmitRef.current(formData);
            }
        }

        if (!isEditing) {
            hasAutoSubmitted.current = false;
        }
    }, [
        isEditing,
        selectedMethod?.id,
        defaultShippingMethodId,
        isLoading,
        customerProfile,
        availableShippingMethods,
        onSubmitRef,
    ]);

    const handleSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            const formData = new FormData(e.currentTarget);
            onSubmitRef.current(formData);
        },
        [onSubmitRef]
    );

    return {
        availableShippingMethods,
        selectedMethod,
        summaryMethod,
        defaultShippingMethodId,
        shippingOptionsError,
        isGuest,
        hideChangeForGuest,
        isUpcomingStep,
        getDiscountedPrice,
        handleSubmit,
    };
}
